import "./InvoiceLineItems.scss";

import { MultiColumnCard } from "components/Card/MultiColumnCard";
import { KaboodleToaster } from "components/common";
import { ControlledTextArea, MaskedInput, TextInput } from "components/inputs";
import { parse } from "date-fns";
import { throttle } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useDeleteInvoiceLineItemMutation,
  useUpdateInvoiceLineItemMutation,
} from "services/invoice-api";
import { DATE_FORMAT } from "utils/date-utils";
import { formatCurrency } from "utils/format-utils";
import { useConstants } from "utils/hooks";

import { ControlledDateInput, ControlledInput } from "../Card/ControlledInput";
import ControlledSelect from "../inputs/Select/ControlledSelect";
import type { InvoiceLineItemType, InvoiceType } from "./InvoiceTypes";
import LineItemTypeSelector from "./LineItemTypeSelector";

const RATE_TYPE_OPTIONS = [
  { value: "flat_fee", label: "Flat" },
  { value: "hourly", label: "Hourly" },
];

type InvoiceLineItemFormValues = {
  id: number;
  invoice: number;
  description: string;
  quantity: string;
  rate: string;
  total: number;
  created: string;
  assignee?: number;
  date?: Date | null;
  rate_type?: string | null;
};

type PresetOption = {
  value: string | null;
  label: string;
};

type InvoiceLineItemRowProps = {
  lineItem: InvoiceLineItemType;
  presetOptions: PresetOption[];
  userOptions: { value: number; label: string }[];
  isFinalized: boolean;
  showRateType: boolean;
};

const InvoiceLineItemRow = ({
  lineItem,
  presetOptions,
  userOptions,
  isFinalized,
  showRateType,
}: InvoiceLineItemRowProps) => {
  const [updateInvoiceLineItem] = useUpdateInvoiceLineItemMutation();

  // determine preset based on description
  const { constants } = useConstants();
  let preset = "custom";
  if (constants?.DEFAULT_INVOICE_PRICING_FIELDS) {
    const matchingPreset = constants.DEFAULT_INVOICE_PRICING_FIELDS.find(
      (option: { label: string; value: string }) =>
        option.label === lineItem.description,
    );
    if (matchingPreset && matchingPreset.value) {
      preset = matchingPreset.value;
    }
  }

  const {
    reset,
    control,
    formState: { errors },
    formState,
    setValue,
  } = useForm<InvoiceLineItemFormValues>({
    defaultValues: {
      id: lineItem.id,
      invoice: lineItem.invoice,
      rate: lineItem.rate.toString(),
      quantity: lineItem.quantity.toString(),
      description: lineItem.description,
      total: lineItem.total,
      created: lineItem.created,
      assignee: lineItem.assignee,
      date: lineItem.date
        ? parse(lineItem.date, DATE_FORMAT, new Date())
        : null,
      rate_type: lineItem.rate_type ?? null,
    },
  });

  const handleFieldSave = useCallback(
    throttle(
      async (
        fieldName: string,
        value: string | number | boolean | null | undefined,
      ) => {
        try {
          const updatedData: Partial<InvoiceLineItemType> = {};

          // convert form values to proper types for API
          if (fieldName === "rate") {
            updatedData.rate =
              typeof value === "string"
                ? parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0
                : Number(value) || 0;
          } else if (fieldName === "quantity") {
            updatedData.quantity =
              typeof value === "string"
                ? parseFloat(value) || 0
                : Number(value) || 0;
          } else if (fieldName === "description") {
            updatedData.description = String(value || "");
          } else if (fieldName === "assignee") {
            updatedData.assignee = value as number | undefined;
          } else if (fieldName === "date") {
            updatedData.date = value as string | undefined;
          } else if (fieldName === "rate_type") {
            updatedData.rate_type = value as string;
          }

          await updateInvoiceLineItem({
            id: lineItem.id,
            data: updatedData,
          }).unwrap();
        } catch (error) {
          console.error("Failed to update line item:", error);
          KaboodleToaster.error("Failed to update line item");
        }
      },
      800,
      { leading: true, trailing: true },
    ),
    [lineItem.id, updateInvoiceLineItem],
  );

  // Preset is now read-only - no need for change handler

  // only reset when the line item ID changes (completely new item)
  useEffect(() => {
    reset({
      id: lineItem.id,
      invoice: lineItem.invoice,
      rate: lineItem.rate.toString(),
      quantity: lineItem.quantity.toString(),
      description: lineItem.description,
      total: lineItem.total,
      created: lineItem.created,
      assignee: lineItem.assignee,
      date: lineItem.date
        ? parse(lineItem.date, DATE_FORMAT, new Date())
        : null,
      rate_type: lineItem.rate_type ?? null,
    });
  }, [reset, lineItem.id]); // only reset when ID changes (new item)

  // sync server updates for non-dirty fields
  useEffect(() => {
    const { dirtyFields } = formState;

    // only update fields that aren't currently being edited
    if (!dirtyFields.rate) {
      setValue("rate", lineItem.rate.toString(), { shouldDirty: false });
    }
    if (!dirtyFields.quantity) {
      setValue("quantity", lineItem.quantity.toString(), {
        shouldDirty: false,
      });
    }
    if (!dirtyFields.description) {
      setValue("description", lineItem.description, { shouldDirty: false });
    }
    if (!dirtyFields.assignee) {
      setValue("assignee", lineItem.assignee, { shouldDirty: false });
    }
    if (!dirtyFields.rate_type) {
      setValue("rate_type", lineItem.rate_type ?? null, {
        shouldDirty: false,
      });
    }
    if (!dirtyFields.date) {
      const serverDate = lineItem.date
        ? parse(lineItem.date, DATE_FORMAT, new Date())
        : null;
      setValue("date", serverDate, { shouldDirty: false });
    }
    // always update total (it's calculated on server)
    setValue("total", lineItem.total, { shouldDirty: false });
  }, [lineItem, setValue, formState.dirtyFields]);

  return (
    <>
      <div className="line-item-type-display">
        {presetOptions.find((option) => option.value === preset)?.label ||
          "Custom"}
      </div>

      <ControlledInput
        control={control}
        name="rate"
        onSave={(value) => handleFieldSave("rate", value)}
        componentType={MaskedInput}
        mask="dollar"
        maxLength={7}
        errors={errors}
        readOnly={isFinalized}
      />

      <ControlledInput
        control={control}
        name="quantity"
        onSave={(value) => handleFieldSave("quantity", value)}
        componentType={MaskedInput}
        mask="float"
        maxLength={7}
        errors={errors}
        readOnly={isFinalized}
      />

      <TextInput
        name="total"
        label={null}
        readOnly={true}
        value={formatCurrency(lineItem.total)}
      />

      <div className="invoice-description-wrapper">
        <div className="invoice-description-sub-grid">
          <figure className="description-label">Description</figure>
          <ControlledTextArea
            control={control}
            name="description"
            label={null}
            onSave={(value: string) => handleFieldSave("description", value)}
            errors={errors}
            readOnly={isFinalized}
            maxLength={500}
            classNames="description-textarea"
          />
        </div>

        <div className="invoice-description-sub-grid">
          <figure className="description-label">Assignee</figure>
          <ControlledSelect
            control={control}
            name="assignee"
            label={null}
            options={userOptions}
            onSave={async (option) => {
              const value = option?.value;
              return handleFieldSave("assignee", value);
            }}
            errors={errors}
            preventClear={false}
            readOnly={isFinalized}
          />
        </div>

        <div className="invoice-description-sub-grid">
          <figure className="description-label">Date</figure>
          <ControlledDateInput
            control={control}
            name="date"
            label={null}
            onSave={(value) => handleFieldSave("date", value)}
            errors={errors}
            readOnly={isFinalized}
            allowFutureDates={true}
          />
        </div>

        {showRateType && (
          <div className="invoice-description-sub-grid">
            <figure className="description-label">Rate Type</figure>
            <ControlledSelect
              control={control}
              name="rate_type"
              label={null}
              options={RATE_TYPE_OPTIONS}
              onSave={async (option) => {
                const value = option?.value ?? null;
                return handleFieldSave("rate_type", value);
              }}
              errors={errors}
              preventClear={true}
              readOnly={isFinalized}
            />
          </div>
        )}
      </div>
    </>
  );
};

type InvoiceLineItemsProps = {
  invoice: InvoiceType;
  isFinalized?: boolean;
};

const InvoiceLineItems = ({
  invoice,
  isFinalized = false,
}: InvoiceLineItemsProps) => {
  const { constants } = useConstants();
  const [deleteInvoiceLineItem] = useDeleteInvoiceLineItemMutation();
  const [showTypeSelector, setShowTypeSelector] = useState(false);

  const showRateType = invoice.invoice_has_rate_type;

  // create user options from constants
  const userOptions = useMemo(() => {
    if (!constants?.USERS) return [];

    return constants.USERS.map((user) => ({
      value: user.value,
      label:
        user.roles?.length > 0
          ? `${user.label} (${user.roles.join(", ")})`
          : user.label,
    }));
  }, [constants?.USERS]);

  // create preset options from constants plus a custom option
  const [presetOptions, setPresetOptions] = useState<PresetOption[]>([
    { value: "custom", label: "Custom" },
  ]);

  useEffect(() => {
    if (constants?.DEFAULT_INVOICE_PRICING_FIELDS) {
      // skip the first "-- Select --" option
      const options = constants.DEFAULT_INVOICE_PRICING_FIELDS.filter(
        (option: { value: string | null; label: string }) =>
          option.value !== null,
      )
        .map((option: { value: string | null; label: string }) => ({
          value: option.value,
          label: option.label,
        }))
        .sort((a: PresetOption, b: PresetOption) =>
          a.label.localeCompare(b.label),
        );

      // always keep "Custom" as the first option
      setPresetOptions([{ value: "custom", label: "Custom" }, ...options]);
    }
  }, [constants]);

  // Fee types archived on the client's fee schedule are hidden from the
  // "new line item" picker (the API also rejects them). Existing rows keep
  // the full list so a line item created before the archive still shows
  // its label instead of falling back to "Custom".
  const clientArchivedPricingFields = invoice.client_archived_pricing_fields;
  const selectablePresetOptions = useMemo(() => {
    const archived = clientArchivedPricingFields ?? [];
    if (archived.length === 0) return presetOptions;
    return presetOptions.filter(
      (option) =>
        option.value === "custom" ||
        option.value === null ||
        !archived.includes(option.value),
    );
  }, [presetOptions, clientArchivedPricingFields]);

  // use line items in their natural order (no sorting)
  const lineItems = invoice.line_items;

  const handleAddNewLineItem = useCallback(() => {
    setShowTypeSelector(true);
  }, []);

  const handleTypeSelectorComplete = useCallback(() => {
    setShowTypeSelector(false);
    // Line item will appear automatically via RTK Query cache invalidation
  }, []);

  const handleTypeSelectorCancel = useCallback(() => {
    setShowTypeSelector(false);
  }, []);

  const handleDeleteLineItem = useCallback(
    async (row: InvoiceLineItemType) => {
      try {
        await deleteInvoiceLineItem({
          id: row.id,
          invoiceId: invoice.id,
        });
        // the line item will disappear automatically via the invoice refresh
      } catch (error) {
        KaboodleToaster.error("Failed to delete line item");
      }
    },
    [deleteInvoiceLineItem, invoice.id],
  );

  const RowComponent = useCallback(
    (lineItem: InvoiceLineItemType) => {
      return (
        <InvoiceLineItemRow
          lineItem={lineItem}
          presetOptions={presetOptions}
          userOptions={userOptions}
          isFinalized={isFinalized}
          showRateType={showRateType}
        />
      );
    },
    [presetOptions, userOptions, isFinalized, showRateType],
  );

  return (
    <>
      <MultiColumnCard
        id="invoice-line-items"
        classNames="invoice-line-items"
        heading="Line Items"
        columnNames={["Type", "Rate", "Quantity", "Total"]}
        initialRows={lineItems}
        RowComponent={RowComponent}
        onNewRow={isFinalized ? () => {} : handleAddNewLineItem}
        onDeleteRow={isFinalized ? undefined : handleDeleteLineItem}
        getRowKey={(row) => row.id}
        isLoading={false}
      />
      {showTypeSelector && (
        <LineItemTypeSelector
          invoice={invoice}
          presetOptions={selectablePresetOptions}
          onComplete={handleTypeSelectorComplete}
          onCancel={handleTypeSelectorCancel}
        />
      )}
    </>
  );
};

export default InvoiceLineItems;
